import { Injectable } from '@angular/core';

/**
 * sessionStorage key holding the event progress filter of this tab.
 *
 * The PHP page kept the same filter in `$_SESSION[uid]['competition_scheduling']`
 * (`Session_CompetitionScheduling`), so the search survived every page change.
 * This service used to be plain in-memory fields, which only survive routing
 * inside the SPA — a reload of `/events/event-progress` re-created the service
 * and the search field came up empty (HIP-64). `Event_scheduling.php` never
 * returns `filter_event_name` in its JSON response, so the value cannot be read
 * back from the PHP session either; the tab has to remember it itself.
 *
 * sessionStorage is the right shelf for it: per-tab and dropped when the tab
 * closes — the same lifetime the PHP session section had, and the same place
 * the `uid` those filters belong to already lives.
 */
export const EVENT_PROGRESS_FILTER_KEY = 'eventProgressFilter';

interface StoredFilter {
    filter_event_name?: string;
    startDate?: string | null;
    endDate?: string | null;
    pmSelected?: string;
    trophyFilter?: string;
    dataServiceByFilter?: string;
}

/** Never throws — sessionStorage can be unavailable (private mode, blocked storage). */
function readStoredFilter(): StoredFilter {
    try {
        const raw = sessionStorage.getItem(EVENT_PROGRESS_FILTER_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

/** Drops the remembered filter, e.g. so the next login in this tab starts clean. */
export function clearEventProgressFilter(): void {
    try {
        sessionStorage.removeItem(EVENT_PROGRESS_FILTER_KEY);
    } catch {}
}

function toDate(value: string | null | undefined): Date | null | undefined {
    if (value === null) {
        return null;
    }
    if (typeof value !== 'string') {
        return undefined;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
}

function toStored(date: Date): string | null {
    return date instanceof Date && !isNaN(date.getTime())
        ? date.toISOString()
        : null;
}

@Injectable({
    providedIn: 'root',
})
export class EventProgressService {
    private _filter_event_name = '';
    private _startDate: Date = new Date();
    private _endDate: Date = new Date();
    private _pmSelected = '0';
    private _trophyFilter = '';
    private _dataServiceByFilter = '';

    constructor() {
        this._startDate.setMonth(this._startDate.getMonth() - 1);
        this._endDate.setMonth(this._endDate.getMonth() + 3);
        this.restore();
    }

    get filter_event_name(): string {
        return this._filter_event_name;
    }

    set filter_event_name(value: string) {
        this._filter_event_name = value;
        this.persist();
    }

    get startDate(): Date {
        return this._startDate;
    }

    set startDate(value: Date) {
        this._startDate = value;
        this.persist();
    }

    get endDate(): Date {
        return this._endDate;
    }

    set endDate(value: Date) {
        this._endDate = value;
        this.persist();
    }

    get pmSelected(): string {
        return this._pmSelected;
    }

    set pmSelected(value: string) {
        this._pmSelected = value;
        this.persist();
    }

    get trophyFilter(): string {
        return this._trophyFilter;
    }

    set trophyFilter(value: string) {
        this._trophyFilter = value;
        this.persist();
    }

    get dataServiceByFilter(): string {
        return this._dataServiceByFilter;
    }

    set dataServiceByFilter(value: string) {
        this._dataServiceByFilter = value;
        this.persist();
    }

    /**
     * Takes over whatever this tab stored before the reload. Anything missing
     * or unreadable keeps the default the constructor just set, so a corrupt
     * entry degrades to "no filter" instead of breaking the page.
     */
    private restore(): void {
        const stored = readStoredFilter();
        if (typeof stored.filter_event_name === 'string') {
            this._filter_event_name = stored.filter_event_name;
        }
        const startDate = toDate(stored.startDate);
        if (startDate !== undefined) {
            // The datepicker may clear a date; the page guards for that itself
            // (`wrongStartDate`), so a cleared date is restored as cleared.
            this._startDate = startDate as Date;
        }
        const endDate = toDate(stored.endDate);
        if (endDate !== undefined) {
            this._endDate = endDate as Date;
        }
        if (typeof stored.pmSelected === 'string') {
            this._pmSelected = stored.pmSelected;
        }
        if (typeof stored.trophyFilter === 'string') {
            this._trophyFilter = stored.trophyFilter;
        }
        if (typeof stored.dataServiceByFilter === 'string') {
            this._dataServiceByFilter = stored.dataServiceByFilter;
        }
    }

    private persist(): void {
        const stored: StoredFilter = {
            filter_event_name: this._filter_event_name,
            startDate: toStored(this._startDate),
            endDate: toStored(this._endDate),
            pmSelected: this._pmSelected,
            trophyFilter: this._trophyFilter,
            dataServiceByFilter: this._dataServiceByFilter,
        };
        try {
            sessionStorage.setItem(
                EVENT_PROGRESS_FILTER_KEY,
                JSON.stringify(stored)
            );
        } catch {}
    }
}
